import { apiClient } from "rp-components"
import { AmenityUnitAvailability, BookingGuest, EmergencyContact, GuestWaiverConfirmation } from "@/models/BookingEvent"

export interface AmenityBooking {
    bookingId: string
    amenityId: string
    amenityUnitId?: string
    amenityName?: string
    propertyId: number
    userId: string
    userEmail: string
    startTime: number
    endTime: number
    status: "confirmed" | "pending_payment" | "payment_failed" | "pending_waiver" | "cancelled"
    guests?: BookingGuest[]
    orderId?: string
    amountInCents?: number | null
    taxAmountInCents?: number | null
    createdAt?: number
}

export interface CreateAmenityBookingRequest {
    propertyId: number
    amenityId: string
    amenityUnitId: string
    amenityName?: string
    userEmail: string
    startTime: number
    endTime: number
    status?: string
    guests?: BookingGuest[]
    emergencyContact?: EmergencyContact
}

export interface UnavailableSlot {
    startTime: number
    endTime: number
    reason?: string
}

class AmenityBookingServiceImpl {
    static instance?: AmenityBookingServiceImpl = undefined

    private constructor() {}

    static getInstance(): AmenityBookingServiceImpl {
        if (AmenityBookingServiceImpl.instance == undefined) {
            AmenityBookingServiceImpl.instance = new AmenityBookingServiceImpl()
        }
        return AmenityBookingServiceImpl.instance
    }

    /**
     * Gets availability of amenity units for a time window.
     * GET properties/{propertyId}/amenities/{amenityId}/amenity-units?since=&until=&excludeBookingId=
     */
    async getAmenityUnitAvailability(
        propertyId: number,
        amenityId: string,
        since: number,
        until: number,
        excludeBookingId?: string
    ): Promise<AmenityUnitAvailability[]> {
        let url = `properties/${propertyId}/amenities/${amenityId}/amenity-units/availability?since=${since}&until=${until}`
        if (excludeBookingId) url += `&excludeBookingId=${excludeBookingId}`
        return apiClient.get(url)
    }

    /**
     * Creates a new amenity booking on a specific unit.
     * POST properties/{propertyId}/amenities/{amenityId}/amenity-units/{amenityUnitId}/bookings
     */
    async createBooking(request: CreateAmenityBookingRequest): Promise<AmenityBooking> {
        const { propertyId, amenityId, amenityUnitId } = request
        const item: any = await apiClient.post(`properties/${propertyId}/amenities/${amenityId}/amenity-units/${amenityUnitId}/bookings`, request)
        if (item?.booking) return { ...item.booking, guests: item.guests }
        return item
    }

    /**
     * Updates an existing amenity booking.
     * PUT properties/{propertyId}/amenity-bookings/{bookingId}
     */
    async updateBooking(propertyId: number, bookingId: string, request: CreateAmenityBookingRequest): Promise<AmenityBooking> {
        const item: any = await apiClient.put(`properties/${propertyId}/amenity-bookings/${bookingId}`, { ...request, bookingId })
        if (item?.booking) return { ...item.booking, guests: item.guests }
        return item
    }

    /**
     * Gets all bookings for an amenity on a given date.
     * GET properties/{propertyId}/amenities/{amenityId}/bookings?date=YYYY-MM-DD
     * Response envelope: { bookings: AmenityBooking[], unavailableSlots: UnavailableSlot[] }
     */
    async getAmenityBookings(
        propertyId: number,
        amenityId: string,
        date: string
    ): Promise<{ bookings: AmenityBooking[]; unavailableSlots: UnavailableSlot[] }> {
        const response = await apiClient.get<{ bookings: any[]; unavailableSlots: any[] }>(
            `properties/${propertyId}/amenities/${amenityId}/bookings?date=${date}`
        )
        const rawBookings: any[] = response?.bookings ?? (Array.isArray(response) ? response : [])
        const bookings = rawBookings.map((item: any) => {
            if (item?.booking) return { ...item.booking, guests: item.guests }
            return item
        })
        const unavailableSlots: UnavailableSlot[] = response?.unavailableSlots ?? []
        return { bookings, unavailableSlots }
    }

    /**
     * Gets the current user's upcoming amenity bookings.
     * GET user/amenity-bookings?notInStatus=cancelled&since={now}
     */
    async getUserUpcomingBookings(): Promise<AmenityBooking[]> {
        const since = Date.now()
        const response = await apiClient.get<any[]>(`user/amenity-bookings?notInStatus=cancelled&since=${since}`)
        return response.map((item: any) => {
            const booking = item.booking || item
            return { ...booking, amenityName: item.amenityName || booking.amenityName, guests: item.guests || booking.guests }
        })
    }

    /**
     * Gets the current user's past amenity bookings.
     * GET user/amenity-bookings?order=desc&until={now}
     */
    async getUserPastBookings(): Promise<AmenityBooking[]> {
        const until = Date.now()
        const response = await apiClient.get<any[]>(`user/amenity-bookings?order=desc&until=${until}`)
        return response.map((item: any) => {
            const booking = item.booking || item
            return { ...booking, amenityName: item.amenityName || booking.amenityName, guests: item.guests || booking.guests }
        })
    }

    /**
     * Gets the full details of a single amenity booking.
     * GET properties/{propertyId}/amenity-bookings/{bookingId}
     */
    async getBookingById(propertyId: number, bookingId: string): Promise<AmenityBooking> {
        const item: any = await apiClient.get(`properties/${propertyId}/amenity-bookings/${bookingId}`)
        if (item?.booking) return { ...item.booking, guests: item.guests || [] }
        return item
    }

    /**
     * Cancels an amenity booking.
     * DELETE properties/{propertyId}/amenity-bookings/{bookingId}
     */
    async cancelBooking(propertyId: number, bookingId: string): Promise<void> {
        return apiClient.delete(`properties/${propertyId}/amenity-bookings/${bookingId}`)
    }

    /**
     * Confirms the waiver acceptance for a guest via their invitation hash.
     * Sends guest details in the payload, matching the admin flow.
     */
    async confirmGuestWaiver(invitationHash: string, guest: BookingGuest): Promise<void> {
        const payload: GuestWaiverConfirmation = {
            firstName: guest.firstName ?? "",
            lastName: guest.lastName ?? "",
            email: guest.email,
            invitationStatus: (guest as any).invitationStatus ?? "pending",
            invitationTimestamp: (guest as any).invitationTimestamp,
            primary: guest.primary ?? false
        }
        await apiClient.post(`public/amenity-bookings/guests/${invitationHash}/confirm`, payload)
    }

    /**
     * Fetches the emergency contact for the authenticated user.
     * GET user/emergency-contact
     */
    async getEmergencyContact(): Promise<EmergencyContact | null> {
        try {
            const result = await apiClient.get<EmergencyContact>(`user/emergency-contact`)
            return result || null
        } catch (err: any) {
            if (err?.response?.status === 404) return null
            throw err
        }
    }

    /**
     * Saves or updates the emergency contact for the authenticated user.
     * PUT user/emergency-contact
     */
    async saveEmergencyContact(contact: EmergencyContact): Promise<void> {
        await apiClient.put(`user/emergency-contact`, contact)
    }
}

export type { AmenityBookingServiceImpl as AmenityBookingService }

export default AmenityBookingServiceImpl.getInstance()
